import { NgClass } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NavigationStart, Router } from "@angular/router";

import { SearchResultType, SearchType } from "@common/plants.enums";

import { FetchService } from "@services/fetch.service";
import { PlantsService } from "@services/plants.service";
import { SearchService } from "@services/search.service";

import { AppConfig } from "../../app.config";
import { Plant } from "../../models/Plant";
import { SearchFilter } from "../../models/SearchFilter";
import { SearchFilteringOption } from "../../models/SearchFilteringOption";
import { TopLevelSearchResult } from "../../models/TopLevelSearchResult";
import { SidebarFilterActionsComponent } from "../../sidebar-filter-actions/sidebar-filter-actions.component";

@Component({
  selector: "top-level-filtering-options",
  templateUrl: "./top-level-filtering-options.component.html",
  styleUrls: ["./top-level-filtering-options.component.css"],
  imports: [NgClass, FormsModule, SidebarFilterActionsComponent]
})
export class TopLevelFilteringOptionsComponent implements OnInit {
  searchResults: Plant[];
  filterOptions: SearchFilteringOption[];
  filterVisibilityMap: Map<string, boolean> = new Map<string, boolean>();
  public showMorphology = true;
  public showGrowthRequirements = true;
  public showReproduction = true;
  public showSuitabilityUse = true;
  public showTopLevel = true;
  public featureFlags: { [key: string]: boolean } = {};

  public checkedCount = 0;
  public hasFiltersBeenApplied = false;

  constructor(
    private appConfig: AppConfig,
    private fetchService: FetchService,
    private searchService: SearchService,
    private plantsService: PlantsService,
    private router: Router
  ) {
    this.featureFlags = this.appConfig.getFeatureFlags();

    this.searchService.searchResultsUpdated$.subscribe(this.updateFilters.bind(this));

    this.searchService.searchType = this.router.url.startsWith("/state-search")
      ? "StateSearch"
      : this.router.url.startsWith("/group-search")
        ? SearchType.GroupSearch
        : this.router.url.startsWith("/duration-search")
          ? SearchType.DurationSearch
          : this.router.url.startsWith("/growth-habit-search")
            ? SearchType.GrowthHabitSearch
            : this.router.url.startsWith("/rarity-search")
              ? SearchType.RaritySearch
              : this.router.url.startsWith("/wetland-search")
                ? SearchType.WetlandSearch
                : this.router.url.startsWith("/noxious-invasive-search")
                  ? SearchType.NoxiousInvasiveSearch
                  : this.router.url.startsWith("/fact-sheet-plant-guide-search-results")
                    ? SearchType.FactSheetPlantGuideSearch
                    : this.router.url.startsWith("/characteristics-search")
                      ? SearchType.CharacteristicsSearch
                      : this.router.url.startsWith("/image-search")
                        ? SearchType.ImageSearch
                        : SearchType.BasicSearch;

    this.router.events.forEach((event) => {
      if (event instanceof NavigationStart) {
        searchService.searchType = this.router.url.startsWith("/state-search")
          ? SearchType.StateSearch
          : this.router.url.startsWith("/group-search")
            ? SearchType.GroupSearch
            : this.router.url.startsWith("/duration-search")
              ? SearchType.DurationSearch
              : this.router.url.startsWith("/growth-habit-search")
                ? SearchType.GrowthHabitSearch
                : this.router.url.startsWith("/rarity-search")
                  ? SearchType.RaritySearch
                  : this.router.url.startsWith("/wetland-search")
                    ? SearchType.WetlandSearch
                    : this.router.url.startsWith("/noxious-invasive-search")
                      ? SearchType.NoxiousInvasiveSearch
                      : this.router.url.startsWith("/fact-sheet-plant-guide-search-results")
                        ? SearchType.FactSheetPlantGuideSearch
                        : this.router.url.startsWith("/characteristics-search")
                          ? SearchType.CharacteristicsSearch
                          : this.router.url.startsWith("/image-search")
                            ? SearchType.ImageSearch
                            : SearchType.BasicSearch;
      }
    });
  }

  ngOnInit(): void {
    // get filters from the shared service
    this.updateFilters();
  }

  // Get all filters.
  getFilters(): SearchFilteringOption[] {
    return this.filterOptions
      ? this.filterOptions.filter(
          (o) =>
            o.Filters &&
            (o.Filters.filter((f) => f.Count > 0).length > 1 ||
              ((o.Name == "Images" ||
                o.Name == "Fact Sheet/Plant Guide" ||
                o.Name == "Characteristics") &&
                o.Filters.filter((f) => f.Count > 0).length >= 1))
        )
      : [];
  }

  getFiltersByType(type: string): SearchFilteringOption[] {
    let filters;
    if (type && type == "Other") {
      filters =
        this.filterOptions && type && typeof type === "string"
          ? this.filterOptions.filter(
              (o) =>
                o.Filters &&
                (o.Filters.filter((f) => f.Count > 0).length >= 1 ||
                  ((o.Name == "Images" ||
                    o.Name == "Fact Sheet/Plant Guide" ||
                    o.Name == "Characteristics") &&
                    o.Filters.filter((f) => f.Count > 0).length >= 1)) &&
                (o.Type === "Filtering" || o.Type == null)
            )
          : null;
    } else {
      filters =
        this.filterOptions && type && typeof type === "string"
          ? this.filterOptions.filter(
              (o) =>
                o.Filters &&
                (o.Filters.filter((f) => f.Count > 0).length > 1 ||
                  ((o.Name == "Images" ||
                    o.Name == "Fact Sheet/Plant Guide" ||
                    o.Name == "Characteristics") &&
                    o.Filters.filter((f) => f.Count > 0).length >= 1)) &&
                o.Type == type
            )
          : null;
    }
    return filters;
  }

  updateFilters(): void {
    if (this.searchService.result) {
      this.searchResults = this.searchService.result.PlantResults;

      // Make deep copy of filter options array so the data binding doesn't affect the
      // filterOptions in the searchService's result map
      this.filterOptions = JSON.parse(JSON.stringify(this.searchService.result.FilterOptions));

      this.filterVisibilityMap.clear();

      if (this.filterOptions) {
        this.filterOptions.forEach((o) => this.filterVisibilityMap.set(o.Name, false));
        this.checkedCount = this.filterOptions.filter(
          (option) => option.Filters.filter((f) => f.IsSelected).length > 0
        ).length;
      }
    }
    this.hasFiltersBeenApplied = false;
  }

  getVisibleFilters(filterOption: SearchFilteringOption): SearchFilter[] {
    if (filterOption.Filters.filter((f) => f.Count > 0).length <= 5)
      return filterOption.Filters.filter((f) => f.Count > 0);
    else return filterOption.Filters.filter((f) => f.Count > 0).slice(0, 5);
  }

  showMoreOrLessFilters(filterOption: SearchFilteringOption): void {
    const currentValue = this.filterVisibilityMap.get(filterOption.Name);

    this.filterVisibilityMap.set(filterOption.Name, !currentValue);
  }

  getMoreFilters(filterOption: SearchFilteringOption): SearchFilter[] {
    if (this.filterVisibilityMap.get(filterOption.Name))
      return filterOption.Filters.filter((f) => f.Count > 0).slice(
        5,
        filterOption.Filters.filter((f) => f.Count > 0).length
      );
    else return [];
  }

  getMoreOrLessFiltersText(filterOption: SearchFilteringOption): string {
    return this.filterVisibilityMap.get(filterOption.Name) ? "Show Less" : "Show More";
  }

  getMoreOrLessFiltersClass(filterOption: SearchFilteringOption): string {
    return this.filterVisibilityMap.get(filterOption.Name)
      ? "fa fa-minus-square-o"
      : "fa fa-plus-square-o";
  }

  toggleFilter(filter: string): void {
    switch (filter) {
      case "Morphology":
        this.showMorphology = !this.showMorphology;
        break;
      case "Growth Requirements":
        this.showGrowthRequirements = !this.showGrowthRequirements;
        break;
      case "Reproduction":
        this.showReproduction = !this.showReproduction;
        break;
      case "Suitability Use":
        this.showSuitabilityUse = !this.showSuitabilityUse;
        break;
      case "TopLevel":
        this.showTopLevel = !this.showTopLevel;
        break;
    }
  }

  updateCheckedCount(): void {
    this.checkedCount = this.checkedCount = this.filterOptions
      .map((option) => option.Filters.filter((f) => f.IsSelected).length)
      .reduce((a, b) => a + b, 0);
    this.hasFiltersBeenApplied = true;
  }

  applyFilters(): void {
    const searchCriteria = this.searchService.searchCriteria;
    searchCriteria.Offset = -1;
    searchCriteria.UnfilteredPlantIds = this.searchService.result.UnfilteredPlantIds;
    searchCriteria.FilterOptions = this.filterOptions;
    this.searchService.searchCriteria = searchCriteria;

    this.searchService.updateSearchStatus(true);

    if (this.searchService.searchType == SearchType.StateSearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.State
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.GroupSearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.Group
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.DurationSearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.Duration
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.GrowthHabitSearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.GrowthHabit
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.RaritySearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.Rarity
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.WetlandSearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.Wetland
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.NoxiousInvasiveSearch) {
      const isInvasiveSearch = this.searchService.searchCriteria.InvasiveLocations.length > 0;
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: isInvasiveSearch ? SearchResultType.Invasive : SearchResultType.Noxious
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.FactSheetPlantGuideSearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.FactSheetPlantGuide
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.CharacteristicsSearch) {
      this.plantsService
        .getPlantsWithCharacteristics(searchCriteria)
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.ImageSearch) {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.Image
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    } else {
      this.fetchService
        .fetchPlantsSearchResults({
          ...searchCriteria,
          Type: SearchResultType.Basic
        })
        .then(this.displayResults.bind(this))
        .catch(this.displayResults.bind(this));
    }
  }

  clearFilters(): void {
    this.filterOptions
      .filter((option) => option.Filters.filter((f) => f.IsSelected))
      .forEach((option) => {
        option.Filters.forEach((f) => (f.IsSelected = false));
      });
    this.checkedCount = 0;
    this.hasFiltersBeenApplied = !this.hasFiltersBeenApplied;
  }

  displayResults(result: TopLevelSearchResult): void {
    this.searchService.updateSearchStatus(false);

    const currentUrl = this.router.url.split("?")[0];

    if (currentUrl != this.router.url) {
      this.router
        .navigate([currentUrl], { queryParams: { resultId: result.ResultId } })
        .then(() => {
          this.searchService.result = result;

          if (!this.searchService.criteriaHistory.has(result.ResultId))
            this.searchService.criteriaHistory.set(
              result.ResultId,
              this.searchService.searchCriteria
            );

          if (!this.searchService.searchTypeHistory.has(result.ResultId))
            this.searchService.searchTypeHistory.set(
              result.ResultId,
              this.searchService.searchType
            );

          if (
            this.searchService.resultHistory.filter((r) => r.ResultId == result.ResultId).length ==
            0
          )
            this.searchService.resultHistory.push(result);
        });
    }
  }
}
